import { OnDestroy, Component, OnInit, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { catchError, EMPTY } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideBell, lucideBellOff, lucideCircleAlert, lucideInbox, lucideInfo } from '@ng-icons/lucide';
import { BrnPopover } from '@spartan-ng/brain/popover';
import { HlmPopover, HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { NotificationService } from '@core/data/service/notification.service';
import { UserService } from '@core/data/service/user.service';
import { NotificationMessage } from '@core/data/model/notification-message';
import { NotificationMessagePatchDto } from '@core/data/model/dto/notification-message-patch-dto';
import { NotificationActionType } from '@core/data/type/notification-action.type';
import { NotificationStatusType } from '@core/data/type/notification-status.type';
import { NotificationPriorityType } from '@core/data/type/notification-priority.type';
import { NotificationType } from '@core/data/type/notification.type';
import { PATH_KEY } from '@core/data/model/notification-action';
import { Page } from '@core/data/model/page';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { lockBodyScroll, unlockBodyScroll } from '@core/utils';
import { platformNotification } from '@shared/components/notifications/platform-notification';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  imports: [
    DatePipe,
    NgIcon,
    HlmPopoverImports,
    HlmSidebarImports,
    HlmSpinnerImports
  ],
  providers: [provideIcons({ lucideBell, lucideBellOff, lucideCircleAlert, lucideInbox, lucideInfo })]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private userService = inject(UserService);
  private router = inject(Router);

  readonly notificationsPopover = viewChild.required(HlmPopover, { read: BrnPopover });

  readonly notifications = signal<NotificationMessage[]>([]);
  readonly unreadCount = signal(0);
  readonly loading = signal(false);
  private currentPage = 0;
  private hasNext = true;

  ngOnInit() {
    this.loadUnreadCount();
  }

  ngOnDestroy() {
    unlockBodyScroll();
  }

  loadUnreadCount() {
    this.notificationService.getCounts()
      .pipe(
        catchError(() => EMPTY)
      )
      .subscribe(count => {
        this.unreadCount.set(count.unread);
      });
  }

  onPopoverStateChanged(state: 'open' | 'closed') {
    if (state === 'closed') {
      unlockBodyScroll();
      return;
    }
    lockBodyScroll();
    if (this.notifications().length === 0) {
      this.resetAndLoadNextPage();
    }
  }

  resetAndLoadNextPage() {
    this.currentPage = 0;
    this.hasNext = true;
    this.notifications.set([]);
    this.loadNextPage();
  }

  loadNextPage() {
    if (!this.hasNext || this.loading()) {
      return;
    }

    this.loading.set(true);

    this.notificationService.searchNotificationMessages(
      undefined,
      undefined,
      this.userService.currentUser?.id,
      this.currentPage,
      Page.DEFAULT_PAGE_SIZE,
      'sentDate',
      'desc'
    )
      .pipe(
        catchError(() => {
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe((page: Page<NotificationMessage>) => {
        this.notifications.update(notifications => [...notifications, ...page.content]);
        this.hasNext = page.number < page.totalPages - 1;
        this.currentPage++;
        this.loading.set(false);
      });
  }

  onNotificationClick(notification: NotificationMessage) {
    if (notification.status !== NotificationStatusType.ACKNOWLEDGED) {
      this.replaceMessage({ ...notification, status: NotificationStatusType.ACKNOWLEDGED });
      this.unreadCount.update(unreadCount => Math.max(0, unreadCount - 1));
      this.markAsAcknowledged(notification);
    }

    const action = notification.notification.action;
    const path = this.platformNotification(notification)?.link
      ?? (action?.type === NotificationActionType.REDIRECT ? action.metadata?.[PATH_KEY] : undefined);
    if (path) {
      this.notificationsPopover().close();
      this.router.navigateByUrl(path);
    }
  }

  /** `notification` is the message as it was before the click; it is put back if the update fails. */
  private markAsAcknowledged(notification: NotificationMessage) {
    const patchDto = new NotificationMessagePatchDto(
      [notification.id],
      notification.readDate || new Date(),
      new Date()
    );

    this.notificationService.updateNotificationMessages(patchDto)
      .pipe(
        catchError(() => {
          this.replaceMessage(notification);
          this.loadUnreadCount();
          return EMPTY;
        })
      )
      .subscribe(updatedMessages => {
        if (updatedMessages.length > 0) {
          this.replaceMessage(updatedMessages[0]);
          this.loadUnreadCount();
        }
      });
  }

  /** Swaps the loaded message with the same id; the list is a signal, so messages are replaced, never mutated. */
  private replaceMessage(message: NotificationMessage) {
    this.notifications.update(notifications => notifications.map(loaded => loaded.id === message.id ? message : loaded));
  }

  onScroll(e: any) {
    const tableViewHeight = e.target?.offsetHeight;
    const tableScrollHeight = e.target?.scrollHeight;
    const scrollLocation = e.target?.scrollTop;

    const buffer = 200;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit) {
      this.loadNextPage();
    }
  }

  getNotificationIcon(message: NotificationMessage) {
    // The platform sends every notification at one priority; its kind tells an ask from an alert.
    if (this.platformNotification(message)) {
      return message.notification.type === NotificationType.ACTIONABLE
        ? { name: 'lucideInbox', color: 'text-info' }
        : { name: 'lucideCircleAlert', color: 'text-warning' };
    }
    const priority = message.notification.priority;

    if (priority === NotificationPriorityType.CRITICAL || priority === NotificationPriorityType.HIGH) {
      return { name: 'lucideCircleAlert', color: 'text-red-500' };
    }
    if (priority === NotificationPriorityType.MEDIUM) {
      return { name: 'lucideInfo', color: 'text-orange-500' };
    }
    if (priority === NotificationPriorityType.LOW) {
      return { name: 'lucideInfo', color: 'text-blue-500' };
    }
    return { name: 'lucideBell', color: 'text-muted-foreground' };
  }

  getNotificationMessage(message: NotificationMessage) {
    return this.platformNotification(message)?.text
      || message.notification.data['message'] || message.renderedContent || '';
  }

  private platformNotification(message: NotificationMessage) {
    return platformNotification(message.notification.key, message.notification.data ?? {});
  }

  isUnread(message: NotificationMessage) {
    return message.status !== NotificationStatusType.ACKNOWLEDGED;
  }
}
